import { isEmpty } from 'lodash'
import dayjs from 'dayjs'

import ArticlePrice from '@Types/entities/ArticlePrice'
import { formatMoney, MONEY_FORMAT_TYPE } from '@Root/utils/moneyFormatter'
import ArticleFloor from '@Types/entities/ArticleFloor'
import ArticleDetail from '@Types/entities/ArticleDetail'
import { FLOOR_INPUT_METHOD, NO_VALUE, TRADE } from '@Root/contants'
import OwnerArticleResponse from '@Types/entities/OwnerArticleResponse'
import AgentProps from '@Types/inner/alarm/AgentProps'

export const getArticleDetailPriceByTradeType = ({ tradeType, articlePrice }: { tradeType: TRADE; articlePrice: ArticlePrice }): string => {
  if (isEmpty(articlePrice)) {
    return ''
  }

  switch (tradeType) {
    case TRADE.DEAL:
      return formatMoney(articlePrice.dealPrice, MONEY_FORMAT_TYPE.MOBILE)
    case TRADE.LEASE:
      return formatMoney(articlePrice.warrantPrice, MONEY_FORMAT_TYPE.MOBILE)
    case TRADE.RENT:
    case TRADE.SHORTERMRENT:
      return `${formatMoney(articlePrice.warrantPrice, MONEY_FORMAT_TYPE.MOBILE)}/${formatMoney(articlePrice.rentPrice, MONEY_FORMAT_TYPE.MOBILE)}`
  }
  return ''
}

export const getArticleDetailFloor = ({
  articleFloor: { floorInputMethodCode, correspondingFloorCount, floorTypeCode, totalFloorCount, undergroundFloorCount, uppergroundFloorCount },
}: {
  articleFloor: ArticleFloor
}) => {
  switch (floorInputMethodCode) {
    case FLOOR_INPUT_METHOD.DIRECT:
    case FLOOR_INPUT_METHOD.HIGH_MIDDLE_LOW:
      return `${correspondingFloorCount}층`
    case FLOOR_INPUT_METHOD.ALL:
      return '전체층'
    case FLOOR_INPUT_METHOD.NO_EXPOSURE:
      return ''
  }

  if (isAvailableFloor(undergroundFloorCount) || isAvailableFloor(uppergroundFloorCount)) {
    return `${uppergroundFloorCount}/${undergroundFloorCount}층`
  }

  if (isAvailableFloor(correspondingFloorCount)) {
    return `${correspondingFloorCount}층`
  }

  return ''
}

const isAvailableFloor = (floorCount: string) => {
  return floorCount && floorCount !== NO_VALUE
}

export const getMoveInTypeName = ({ articleDetail: { moveInTypeName, moveInPossibleYmd } }: { articleDetail: ArticleDetail }) => {
  if (moveInPossibleYmd) {
    if (moveInPossibleYmd === 'NOW') {
      return '즉시입주'
    }
    return dayjs(moveInPossibleYmd).format('YYYY.MM.DD')
  }

  if (!moveInTypeName) {
    return '-'
  }

  if (!moveInTypeName.trim().match(/[^이내|이후]/g)) {
    return ''
  }

  return moveInTypeName
}

export const makePlaceList = ({ articleFloor, articleFacility, articleDetail }: Pick<OwnerArticleResponse, 'articleFloor' | 'articleFacility' | 'articleDetail'>) => {
  const placeList = {
    direction: {
      title: '방향',
      data: `${articleFacility.directionTypeName || ''} ${(articleFacility.directionBaseTypeName && `(${articleFacility.directionBaseTypeName})`) || ''}`.trim(),
    },
    floor: {
      title: '층정보',
      data: articleDetail.articleTypeCode !== 'E03' ? getArticleDetailFloor({ articleFloor }) : '',
    },
    placeDate: {
      title: '입주가능일',
      data: `${getMoveInTypeName({ articleDetail }) || ''} ${articleDetail.moveInDiscussionPossibleYN || ''}`.trim(),
    },
    exposeStartDate: {
      title: '노출시작일',
      data: dayjs(articleDetail.exposeStartYMD).format('YYYY.MM.DD'),
    },
  }

  return Object.entries(placeList).reduce((acc, [key, value]) => {
    if (value.data) {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, { title: string; data: string }>)
}

export const makeModifyPagePlaceList = ({ articleFloor, articleFacility, articleDetail }: Pick<OwnerArticleResponse, 'articleFloor' | 'articleFacility' | 'articleDetail'>) => {
  const placeList = {
    direction: {
      title: '방향',
      data: `${articleFacility.directionTypeName || ''} ${(articleFacility.directionBaseTypeName && `(${articleFacility.directionBaseTypeName})`) || ''}`.trim(),
    },
    floor: {
      title: '층정보',
      data: articleDetail.articleTypeCode !== 'E03' ? getArticleDetailFloor({ articleFloor }) : '',
    },
    placeDate: {
      title: '입주가능일',
      data: `${getMoveInTypeName({ articleDetail }) || ''} ${articleDetail.moveInDiscussionPossibleYN || ''}`.trim(),
    },
  }

  return Object.entries(placeList).reduce((acc, [key, value]) => {
    if (value.data) {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, { title: string; data: string }>)
}

export const getNaverMapFindRouteUrl = (destination: string, latitude: number, longitude: number, departure?: string, departurelat?: number, departurelng?: number) => {
  if (departure) {
    return (
      `https://map.naver.com/index.nhn?enc=utf8&eText=${encodeURI(destination)}&elat=${latitude}&elng=${longitude}&pinType=site&menu=route&dlevel=12` +
      `&sText=${encodeURI(departure)}&slat=${departurelat}&slng=${departurelng}`
    )
  }
  return `https://map.naver.com/index.nhn?enc=utf8&eText=${encodeURIComponent(destination)}&elat=${latitude}&elng=${longitude}&pinType=site&menu=route&dlevel=12`
}

export const makeAgent = ({ articleRealtor }: Pick<OwnerArticleResponse, 'articleRealtor'>): AgentProps => {
  const naverMapLink = getNaverMapFindRouteUrl(articleRealtor?.realtorName?.replace(/%/g, ' '), articleRealtor?.latitude, articleRealtor?.longitude)
  return {
    agentTitle: articleRealtor.realtorName,
    agentPosition: '대표',
    agentManager: articleRealtor.representativeName,
    agentPhone: articleRealtor.representativeTelNo,
    agentLocation: naverMapLink,
  }
}
export const m2ToPyeongName = (m2: string) => {
  if (!m2) {
    return ''
  }
  const area = parseFloat(m2)
  if (!area) {
    return ''
  }
  const type = m2.replace(area.toString(), '')
  return Math.floor(area / 3.305785) > 0 ? Math.floor(area / 3.305785) + type : ''
}

export const stringListJoinAfterTrim = (list: string[]): string => {
  if (isEmpty(list)) {
    return ''
  }

  return list
    .map((value) => value && value.trim())
    .filter((value) => !!value)
    .join(' ')
}

export const getDateFormat = (value: string): string => {
  if (!value) {
    return ''
  }
  const inputTradeDate = value.replace(/[^0-9]/g, '')
  const match = inputTradeDate.match(/([0-9999]{0,4})?([0-1]{1})?([0-9]{1})?([0-3]{1})?([0-9]{1})?/)
  if (match === null) {
    return ''
  }
  let year = ''
  if (typeof match[1] !== 'undefined' && parseInt(match[1], 10) > 0) {
    year = match[1]
  }
  let month = ''
  if (typeof match[2] !== 'undefined' && typeof match[3] !== 'undefined' && parseInt(match[2] + match[3], 10) > 0 && parseInt(match[2] + match[3], 10) < 13) {
    month = '-' + match[2] + match[3]
  } else if (typeof match[3] !== 'undefined' && parseInt(match[3], 10) > 0) {
    month = '-0' + parseInt(match[3], 10)
  } else if (typeof match[2] !== 'undefined') {
    month = '-' + parseInt(match[2], 10)
  }
  let day = ''
  if (typeof match[4] !== 'undefined' && typeof match[5] !== 'undefined' && parseInt(match[4] + match[5], 10) > 0 && parseInt(match[4] + match[5], 10) < 32) {
    day = '-' + match[4] + match[5]
  } else if (typeof match[5] !== 'undefined' && parseInt(match[5], 10) > 0) {
    day = '-0' + parseInt(match[5], 10)
  } else if (typeof match[4] !== 'undefined') {
    day = '-' + parseInt(match[4], 10)
  }
  return year + month + day
}

export const getNumberWithoutText = (value: string): number => {
  const numberValue = parseInt(value.replace(/[^\d]+/g, ''), 10)
  if (isNaN(numberValue)) {
    return 0
  }
  return numberValue
}
export const getTextWithoutSpecialText = (value: string): string => {
  if (!value) {
    return ''
  }
  return value.replace(/[^0-9|가-힣|ㄱ-ㅎ|ㅏ-ㅣ|a-z|A-Z|\u318D\u119E\u11A2\u2022\u2025a\u00B7\uFE55| ]/g, '') || ''
}
